import React, { useCallback, useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, Star, ChevronLeft, ChevronRight, CheckCircle2, ArrowRight, Pause, Play } from "lucide-react";
import { heroData, heroSlides, navData } from "../../data/mockData";

const SLIDE_MS = 6500;

/* ── Slider State ── */

function useHeroSlider(count) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const go = useCallback((i) => setIndex(((i % count) + count) % count), [count]);
  const next = useCallback(() => setIndex((i) => (i + 1) % count), [count]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + count) % count), [count]);
  return { index, paused, setPaused, go, next, prev };
}

/* ── Slide Text ── */

const SlideText = ({ slide, active, scrollToQuote }) => (
  <div
    className={`hero-slide-text space-y-6 ${active ? "is-active" : ""}`}
    aria-hidden={!active}
    inert={!active}
    data-testid={`hero-slide-text-${slide.id}`}
  >
    <span className="hero-anim inline-flex items-center gap-2 rounded-full bg-cleanora-primary/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-cleanora-primary-dark">
      <span className="h-1.5 w-1.5 rounded-full bg-cleanora-primary" />
      {slide.eyebrow}
    </span>
    <h2 className="hero-anim text-3xl sm:text-4xl xl:text-[2.75rem] font-extrabold leading-[1.1] tracking-tight text-slate-900">
      {slide.title}{" "}
      <span className="text-transparent bg-clip-text bg-gradient-to-r from-cleanora-primary to-cleanora-primary-dark">
        {slide.highlight}
      </span>
    </h2>
    <p className="hero-anim max-w-xl text-base md:text-lg leading-relaxed text-slate-600">{slide.subtitle}</p>
    <ul className="hero-anim flex flex-wrap gap-x-5 gap-y-2">
      {slide.points.map((pt) => (
        <li key={pt} className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <CheckCircle2 className="h-4 w-4 text-cleanora-accent" /> {pt}
        </li>
      ))}
    </ul>
    <div className="hero-anim flex flex-wrap items-center gap-3">
      <button
        onClick={scrollToQuote}
        tabIndex={active ? 0 : -1}
        data-testid={`hero-quote-btn-${slide.id}`}
        className="group inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-cleanora-primary to-cleanora-primary-dark px-8 py-4 text-lg font-bold text-white shadow-xl transition-transform duration-300 hover:scale-[1.03]"
      >
        Get Free Quote
        <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
      </button>
      {slide.link && (
        <Link
          to={slide.link}
          tabIndex={active ? 0 : -1}
          data-testid={`hero-learn-more-${slide.id}`}
          className="inline-flex items-center gap-1 px-2 py-4 font-bold text-cleanora-primary-dark underline-offset-4 hover:underline"
        >
          {slide.linkLabel}
        </Link>
      )}
    </div>
  </div>
);

/* ── Slide Images ── */

const SlideImages = ({ index }) => (
  <div className="relative -mx-4 aspect-[20/9] w-[calc(100%+2rem)] overflow-hidden rounded-2xl bg-sky-50 shadow-2xl ring-1 ring-cleanora-primary/15 sm:mx-0 sm:w-full sm:rounded-3xl lg:aspect-auto lg:h-[470px] xl:h-[520px]">
    {heroSlides.map((slide, i) => (
      <div key={slide.id} className={`hero-slide-img absolute inset-0 ${i === index ? "is-active" : ""}`} aria-hidden={i !== index}>
        <img src={slide.image} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full scale-125 object-cover opacity-50 blur-3xl saturate-150" />
        <img
          src={slide.image}
          alt={slide.alt}
          loading={i === 0 ? "eager" : "lazy"}
          fetchPriority={i === 0 ? "high" : undefined}
          data-testid={`hero-slide-image-${slide.id}`}
          className={`relative h-full w-full object-contain drop-shadow-2xl ${i === index ? "is-active" : ""}`}
        />
      </div>
    ))}
  </div>
);

/* ── Controls ── */

const ArrowButton = ({ onClick, label, icon: Icon, testId }) => (
  <button
    onClick={onClick}
    aria-label={label}
    data-testid={testId}
    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition-colors duration-200 hover:border-cleanora-primary hover:bg-cleanora-primary hover:text-white"
  >
    <Icon className="h-5 w-5" />
  </button>
);

const SlideTabs = ({ index, paused, go, onDone }) => (
  <div className="flex min-w-0 flex-1 gap-2 sm:gap-3" role="tablist" aria-label="Choose a service">
    {heroSlides.map((slide, i) => (
      <button
        key={slide.id}
        role="tab"
        aria-selected={i === index}
        onClick={() => go(i)}
        data-testid={`hero-slide-tab-${slide.id}`}
        className="group min-w-0 flex-1 py-2 text-left"
      >
        <span className="block h-1 overflow-hidden rounded-full bg-slate-200">
          {i === index && (
            <span
              key={index}
              className="hero-progress block h-full rounded-full bg-cleanora-primary"
              style={{ animationDuration: `${SLIDE_MS}ms`, animationPlayState: paused ? "paused" : "running" }}
              onAnimationEnd={onDone}
            />
          )}
          {i < index && <span className="block h-full w-full rounded-full bg-cleanora-primary/40" />}
        </span>
        <span className={`mt-2 hidden truncate text-[11px] font-bold uppercase tracking-wide sm:block transition-colors duration-200 ${i === index ? "text-cleanora-primary-dark" : "text-slate-400 group-hover:text-slate-600"}`}>
          {slide.tab}
        </span>
      </button>
    ))}
  </div>
);

const SliderControls = ({ slider }) => (
  <div className="mt-6 flex items-center gap-3 sm:gap-4">
    <SlideTabs index={slider.index} paused={slider.paused} go={slider.go} onDone={slider.next} />
    <button
      onClick={() => slider.setPaused((p) => !p)}
      aria-label={slider.paused ? "Play slideshow" : "Pause slideshow"}
      data-testid="hero-play-pause-btn"
      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-slate-500 transition-colors duration-200 hover:text-cleanora-primary"
    >
      {slider.paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
    </button>
    <ArrowButton onClick={slider.prev} label="Previous slide" icon={ChevronLeft} testId="hero-prev-btn" />
    <ArrowButton onClick={slider.next} label="Next slide" icon={ChevronRight} testId="hero-next-btn" />
  </div>
);

/* ── Contact Row + Trust ── */

const ContactRow = () => (
  <div className="flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-slate-200/80 pt-6">
    <a href={`tel:${navData.phoneLink}`} data-testid="hero-call-btn" className="inline-flex items-center gap-2 font-bold text-slate-700 transition-colors hover:text-cleanora-primary">
      <Phone className="h-4 w-4 text-cleanora-primary" /> Call {navData.phone}
    </a>
    <a href={navData.whatsappLink} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-btn" className="inline-flex items-center gap-2 rounded-full bg-cleanora-accent px-4 py-2 text-sm font-bold text-white shadow transition-transform duration-200 hover:scale-105">
      <MessageCircle className="h-4 w-4" /> WhatsApp Us
    </a>
    <div className="flex items-center gap-2">
      <div className="flex gap-0.5">
        {[...Array(5)].map((_, i) => (
          <Star key={`hero-star-${i}`} className="h-4 w-4 fill-amber-400 text-amber-400" />
        ))}
      </div>
      <span className="text-sm font-medium text-slate-600">{heroData.trustText}</span>
    </div>
  </div>
);

/* ── Page Headline ── */

const AUDIENCES = ["Homeowners", "Landlords", "Letting agents", "Airbnb hosts", "Offices & businesses"];

const HeroHeadline = () => (
  <div className="mb-8 flex flex-col gap-3 border-b border-slate-200/80 pb-6 lg:flex-row lg:items-end lg:justify-between" data-testid="hero-headline">
    <h1 className="max-w-3xl text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900" data-testid="home-h1">
      Professional Domestic & Commercial Cleaning Services in <span className="text-cleanora-primary">Cardiff, Barry and Penarth</span>.
    </h1>
    <ul className="flex flex-wrap gap-2" aria-label="Who we clean for">
      {AUDIENCES.map((a) => (
        <li key={a} className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700">{a}</li>
      ))}
    </ul>
  </div>
);

/* ── Hero Slider ── */

const HeroSlider = ({ scrollToQuote }) => {
  const slider = useHeroSlider(heroSlides.length);
  const touchX = useRef(null);
  const [hovering, setHovering] = useState(false);
  const [hidden, setHidden] = useState(false);
  const { next, prev } = slider;

  useEffect(() => {
    const onVis = () => setHidden(document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const onKeyDown = (e) => {
    if (e.key === "ArrowRight") next();
    if (e.key === "ArrowLeft") prev();
  };
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 50) (dx < 0 ? next : prev)();
    touchX.current = null;
  };
  const effective = { ...slider, paused: slider.paused || hovering || hidden };

  return (
    <div
      role="region"
      aria-roledescription="carousel"
      aria-label="NeatWave cleaning services"
      tabIndex={0}
      onKeyDown={onKeyDown}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={onTouchEnd}
      className="grid items-center gap-x-10 gap-y-10 outline-none lg:grid-cols-[minmax(0,4fr)_minmax(0,8fr)]"
      data-testid="hero-slider"
    >
      <div className="space-y-5 lg:col-start-1 lg:row-start-1 lg:self-end">
        <div className="grid" aria-live={effective.paused ? "polite" : "off"}>
          {heroSlides.map((slide, i) => (
            <SlideText key={slide.id} slide={slide} active={i === slider.index} scrollToQuote={scrollToQuote} />
          ))}
        </div>
      </div>
      <div className="order-first lg:order-none lg:col-start-2 lg:row-span-2 lg:row-start-1">
        <SlideImages index={slider.index} />
        <SliderControls slider={effective} />
      </div>
      <div className="lg:col-start-1 lg:row-start-2 lg:self-start">
        <ContactRow />
      </div>
    </div>
  );
};

/* ── Wave Divider ── */

const HeroWave = () => (
  <div className="absolute bottom-0 left-0 w-full pointer-events-none" aria-hidden="true" data-testid="hero-wave-divider">
    <svg className="hero-wave hero-wave-back" viewBox="0 0 2880 120" preserveAspectRatio="none">
      <path d="M0 60 C240 10 480 10 720 60 S1200 110 1440 60 S1920 10 2160 60 S2640 110 2880 60 V120 H0Z" fill="#0EA5E9" fillOpacity="0.18" />
    </svg>
    <svg className="hero-wave hero-wave-mid" viewBox="0 0 2880 120" preserveAspectRatio="none">
      <path d="M0 75 C240 35 480 35 720 75 S1200 115 1440 75 S1920 35 2160 75 S2640 115 2880 75 V120 H0Z" fill="#0EA5E9" fillOpacity="0.35" />
    </svg>
    <svg className="hero-wave hero-wave-front" viewBox="0 0 2880 120" preserveAspectRatio="none">
      <path d="M0 90 C240 60 480 60 720 90 S1200 120 1440 90 S1920 60 2160 90 S2640 120 2880 90 V120 H0Z" fill="#FFFFFF" />
    </svg>
  </div>
);

/* ── Main Section ── */

const HeroSection = ({ scrollToQuote }) => (
  <section className="relative bg-gradient-to-br from-cleanora-soft-mist via-white to-slate-50 pt-12 sm:pt-16 lg:pt-20 pb-32 sm:pb-40 overflow-hidden" data-testid="hero-section">
    <HeroWave />
    {/* Decorative background elements */}
    <div className="absolute top-0 right-0 w-96 h-96 bg-cleanora-primary/5 rounded-full blur-3xl"></div>
    <div className="absolute bottom-0 left-0 w-96 h-96 bg-cleanora-accent/5 rounded-full blur-3xl"></div>
    
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      <HeroHeadline />
      <HeroSlider scrollToQuote={scrollToQuote} />
    </div>
  </section>
);

export default HeroSection;
